import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Header } from "../components/app/Header";
import { ExportTools } from "../components/app/ExportTools";
import { ShareToCommunity } from "../components/app/ShareToCommunity";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { ArrowLeft, ArrowUpRight, ArrowDownRight, MinusSquare, AlertTriangle, Activity, Loader2, ShieldX } from "lucide-react";

const DirectionMeta = (d) => {
  if (d === "BULLISH" || d === "UP") return { color: "text-bull", glow: "glow-bull", Icon: ArrowUpRight, key: "BULLISH" };
  if (d === "BEARISH" || d === "DOWN") return { color: "text-bear", glow: "glow-bear", Icon: ArrowDownRight, key: "BEARISH" };
  return { color: "text-neutral-400", glow: "", Icon: MinusSquare, key: "NEUTRAL" };
};

const confidenceColor = (c) => {
  if (c >= 70) return { bar: "bg-bull", text: "text-bull" };
  if (c >= 55) return { bar: "bg-warn", text: "text-warn" };
  return { bar: "bg-bear", text: "text-bear" };
};

export default function AnalysisDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!authLoading && !user) navigate("/login", { replace: true });
  }, [user, authLoading, navigate]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/analyses/${id}`);
        setData(data);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Analysis not found");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-bull" /></div>;
  if (err) return (
    <div className="min-h-screen">
      <Header />
      <div className="mx-auto max-w-2xl p-10 text-center">
        <p className="text-neutral-400">{err}</p>
        <Link to="/dashboard" className="mt-4 inline-block font-mono text-sm text-bull">← {t("back_btn")}</Link>
      </div>
    </div>
  );

  const meta = DirectionMeta(data.direction);
  const cc = confidenceColor(data.confidence);
  const kl = data.key_levels || { support: [], resistance: [] };
  const userPlan = user?.role === "OWNER" ? "OWNER" : (user?.plan || "FREE");
  const canExport = userPlan === "TRADER" || userPlan === "PRO" || userPlan === "OWNER";

  return (
    <div className="min-h-screen" data-testid="analysis-detail-page">
      <Header />
      <main className="mx-auto max-w-7xl px-6 md:px-8 py-8 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} className="text-neutral-400 hover:text-white hover:bg-white/5" data-testid="back-btn">
            <ArrowLeft className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> {t("back_btn")}
          </Button>
          <div className="flex items-center gap-3 flex-wrap">
            <ShareToCommunity analysisId={data.analysis_id} userPlan={userPlan} t={t} />
            <ExportTools analysis={data} canExport={canExport} />
            {data.trading_style && (
              <span className="px-2.5 h-7 inline-flex items-center border border-white/10 text-neutral-300 font-mono text-xs tracking-wider">
                {data.trading_style.replace("_", " ")}
              </span>
            )}
            <span className="text-neutral-300 font-mono text-xs">{data.analysis_id}</span>
          </div>
        </div>

        <div id="analysis-export-target" className="space-y-6">

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* Chart */}
          <div className="lg:col-span-7 surface-card overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
              <span className="label-mono">CHART // {data.symbol || "UNTAGGED"} {data.timeframe ? `· ${data.timeframe}` : ""}</span>
              <span className="font-mono text-[11px] text-neutral-500">{new Date(data.created_at).toLocaleString()}</span>
            </div>
            <div className="bg-black aspect-[16/10] flex items-center justify-center">
              {data.thumbnail ? <img src={data.thumbnail} alt="chart" className="max-w-full max-h-full object-contain" data-testid="analysis-thumbnail" /> :
                <span className="label-mono">NO_PREVIEW</span>}
            </div>
          </div>

          {/* Prediction + Levels */}
          <div className="lg:col-span-5 space-y-4">
            <div className={`surface-card p-6 ${meta.glow}`} data-testid="prediction-card">
              <span className="label-mono">{t("prediction_label")}</span>
              <div className="mt-4 flex items-baseline gap-3">
                <meta.Icon className={`h-10 w-10 ${meta.color}`} strokeWidth={1.5} />
                <span className={`font-mono text-5xl font-medium ${meta.color}`} data-testid="direction-text">{t(`direction_${meta.key}`)}</span>
              </div>
              <div className="mt-4 font-mono text-sm flex items-center justify-between">
                <span className="text-neutral-500">{t("confidence_label")}</span>
                <span className={cc.text} data-testid="confidence-text">{data.confidence}%</span>
              </div>
              <div className="mt-2 h-2 bg-white/5 overflow-hidden">
                <div className={`h-full ${cc.bar}`} style={{ width: `${data.confidence}%` }} />
              </div>
              {data.market_structure && (
                <div className="mt-5">
                  <span className="label-mono block mb-1.5">{t("market_structure_label")}</span>
                  <span className="font-mono text-xs px-2.5 py-1.5 inline-block border border-white/10 bg-white/[0.03] text-neutral-200 break-words leading-relaxed">{data.market_structure}</span>
                </div>
              )}
              {data.analysis_summary && (
                <p className="mt-5 text-sm text-neutral-300 leading-relaxed">{data.analysis_summary}</p>
              )}
            </div>

            {/* Levels */}
            <div className="surface-card p-6" data-testid="levels-card">
              <div className="flex items-center justify-between mb-3">
                <span className="label-mono">{t("levels_label")}</span>
                {data.risk_reward && (
                  <div className="text-right">
                    <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-neutral-500">{t("rr_label")}</div>
                    <div className={`font-mono text-2xl ${meta.color}`}>{data.risk_reward}</div>
                  </div>
                )}
              </div>
              <div className="divide-y divide-white/5">
                {[
                  { label: t("entry"), value: data.entry, color: "text-white" },
                  { label: t("tp1"), value: data.tp1, color: "text-bull" },
                  { label: t("tp2"), value: data.tp2, color: "", style: { color: "#FF8C00" } },
                  { label: t("stop_loss"), value: data.stop_loss, color: "text-bear" },
                ].map((lv, i) => (
                  <div key={i} className="py-3 flex items-baseline justify-between gap-4" data-testid={`level-${i}`}>
                    <span className="font-mono text-xs uppercase tracking-wider text-neutral-400">{lv.label}</span>
                    <span className={`font-mono text-lg ${lv.color}`} style={lv.style || {}}>{lv.value || "—"}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Key levels (support / resistance) */}
        {(kl.support?.length > 0 || kl.resistance?.length > 0) && (
          <div className="surface-card p-6" data-testid="key-levels-card">
            <span className="label-mono">{t("key_levels_label")}</span>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-bull mb-2">{t("support")}</div>
                <div className="flex flex-wrap gap-2">
                  {(kl.support || []).map((s, i) => (
                    <span key={i} className="font-mono text-sm px-3 py-1.5 border border-bull/30 bg-bull/[0.05] text-bull">{s}</span>
                  ))}
                  {(kl.support?.length ?? 0) === 0 && <span className="text-sm text-neutral-500">—</span>}
                </div>
              </div>
              <div>
                <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-bear mb-2">{t("resistance")}</div>
                <div className="flex flex-wrap gap-2">
                  {(kl.resistance || []).map((s, i) => (
                    <span key={i} className="font-mono text-sm px-3 py-1.5 border border-bear/30 bg-bear/[0.05] text-bear">{s}</span>
                  ))}
                  {(kl.resistance?.length ?? 0) === 0 && <span className="text-sm text-neutral-500">—</span>}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Patterns + candlesticks */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="surface-card p-6" data-testid="patterns-card">
            <span className="label-mono">{t("patterns_label")}</span>
            <div className="mt-4 flex flex-wrap gap-2">
              {(data.detected_patterns || []).length === 0 && <span className="text-sm text-neutral-500">{t("none_identified")}</span>}
              {(data.detected_patterns || []).map((p, i) => (
                <span key={i} className="inline-flex items-center gap-2 px-3 py-1.5 border border-bull/20 bg-bull/[0.04] font-mono text-xs text-bull">
                  <Activity className="h-3 w-3" strokeWidth={2} />{p}
                </span>
              ))}
            </div>
          </div>
          <div className="surface-card p-6" data-testid="candlestick-patterns-card">
            <span className="label-mono">{t("candle_patterns_label")}</span>
            <div className="mt-4 flex flex-wrap gap-2">
              {(data.candlestick_patterns || []).length === 0 && <span className="text-sm text-neutral-500">{t("none_identified")}</span>}
              {(data.candlestick_patterns || []).map((p, i) => (
                <span key={i} className="inline-flex items-center px-3 py-1.5 border border-white/10 bg-white/[0.03] font-mono text-xs text-neutral-200">
                  {p}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Trading style notes */}
        {data.trading_style_notes && (
          <div className="surface-card p-6" data-testid="style-notes-card">
            <span className="label-mono">{t("style_notes_label")}</span>
            <p className="mt-3 text-sm text-neutral-300 leading-relaxed">{data.trading_style_notes}</p>
          </div>
        )}

        {/* Invalidation */}
        {data.invalidation && (
          <div className="border border-warn/30 bg-warn/[0.05] p-5 flex items-start gap-3" data-testid="invalidation-card">
            <ShieldX className="h-5 w-5 text-warn shrink-0" strokeWidth={1.5} />
            <div>
              <div className="label-mono text-warn">{t("invalidation_label")}</div>
              <p className="mt-1 text-sm text-neutral-200 leading-relaxed">{data.invalidation}</p>
            </div>
          </div>
        )}

        {/* Disclaimer */}
        <div className="border border-white/10 bg-white/[0.02] p-4 flex items-start gap-3" data-testid="disclaimer">
          <AlertTriangle className="h-4 w-4 text-neutral-500 shrink-0 mt-0.5" strokeWidth={1.5} />
          <p className="text-[11px] font-mono text-neutral-500 leading-relaxed">{t("disclaimer")}</p>
        </div>
        </div>
      </main>
    </div>
  );
}
